/**
 * The pane in a window opening, and nothing else.
 *
 * This used to draw the weather as well: a box mesh filling the opening, carrying a
 * scrolling storm texture. That box was the whole problem. It sat *in the wall plane*, so
 * it had no distance from the player and no parallax against the reveal, and every window
 * carried its own copy, so the world south of the house and the world west of it were
 * unrelated pictures. Deleting it turns each opening back into the hole the wall geometry
 * always said it was, and `ExteriorWorld` is now visible through it — one world, actually
 * far away, shared by every window in the house.
 *
 * What is left is a sheen, not a surface: barely opaque, writing no depth so the exterior
 * behind it and the glazing bars in front both stay unsorted and correct. It is not what
 * stops a thrown box — that is the collider `windowGlassBoxes` puts in the same opening,
 * which is separate geometry and is untouched by any of this. This only makes the glass
 * legible.
 */

import { DoubleSide } from 'three';
import type { WindowFace } from '../game/profile/buildGeometry';
import { WALL_THICKNESS } from '../game/profile/buildGeometry';

type Props = {
  faces: readonly WindowFace[];
};

const GLASS_TINT = '#cfe0f2';
/**
 * Down from 0.13, because the exterior behind it went black.
 *
 * A 13% wash of pale blue is invisible over a mid-tone storm texture and obvious over
 * nothing: composited onto black it lands around `#1b1d1f`, which is why the ground read as
 * grey rather than black however black it was actually painted. The pane still has to be
 * *visible* — a window with no glass at all reads as an open hole — so this is as low as it
 * goes while leaving a sheen on the surface.
 */
const GLASS_OPACITY = 0.05;
/** Just clear of the wall's inner face, under the bars. */
const GLASS_INSET = 0.01;

export function WindowGlass({ faces }: Props) {
  if (faces.length === 0) return null;

  return (
    <>
      {faces.map((face) => {
        const acrossX = face.inward.x !== 0;
        const paneOffset = WALL_THICKNESS / 2 + GLASS_INSET;

        return (
          <mesh
            key={`window-${face.windowId}`}
            position={[
              face.center.x + face.inward.x * paneOffset,
              face.center.y,
              face.center.z + face.inward.z * paneOffset,
            ]}
            rotation={[0, acrossX ? Math.PI / 2 : 0, 0]}
          >
            <planeGeometry args={[face.width, face.head - face.sill]} />
            {/* Double-sided so the pane needs no per-wall sign: which way `inward` points
                decides nothing about a sheet of glass. */}
            <meshBasicMaterial
              color={GLASS_TINT}
              transparent
              opacity={GLASS_OPACITY}
              depthWrite={false}
              side={DoubleSide}
            />
          </mesh>
        );
      })}
    </>
  );
}
